import { Intent as BlueprintIntent, Callout } from '@blueprintjs/core';
import * as React from 'react';
import { useParams } from 'react-router-dom';
import {
  Button,
  Input,
  LoadingIcon,
  Modal,
} from 'vet-bones/design-system/components';

import { useAppDispatch, useAppSelector } from 'src/ui/app/hooks';
import {
  CANCEL_ENDPOINT_DIALOG_CANCEL,
  CANCEL_ENDPOINT_DIALOG_INPUT,
  CANCEL_ENDPOINT_DIALOG_SUBMIT,
} from 'src/ui/constants/testIds';
import { useInvalidateConnections } from 'src/ui/containers/endpoints/utils/invalidate';
import { queueMessage, queueNotification } from 'src/ui/features/notifications';
import { DialogType, closedDialog } from 'src/ui/features/ui';
import { graphQLClient } from 'src/ui/graph/graphQLClient';
import { useCancelCloudMutation } from 'src/ui/graph/types';
import { useCurrentConnection } from 'src/ui/hooks/connection';
import { useOnError, useOnMutate } from 'src/ui/hooks/graph';
import { editIndexSelector } from 'src/ui/selectors/connection';
import { isDialogOpenSelector } from 'src/ui/selectors/isDialogOpen';
import * as copy from 'src/ui/templates/copy';

const CANCEL_CONFIRMATION = 'CANCEL';

const isOpenSelector = isDialogOpenSelector(DialogType.CANCEL_ENDPOINT);

export const CancelEndpointDialog: React.VFC = () => {
  const dispatch = useAppDispatch();
  const { org = '' } = useParams<{ org?: string }>();

  const isOpen = useAppSelector(isOpenSelector);
  const editIndex = useAppSelector(editIndexSelector);
  const onError = useOnError();
  const onMutate = useOnMutate();

  const { connection } = useCurrentConnection(editIndex.toString(), org);
  const invalidateConnections = useInvalidateConnections(org);

  const [value, setValue] = React.useState('');

  const { isLoading, mutate: cancelCloud } = useCancelCloudMutation(
    graphQLClient,
    {
      onMutate,
      onError,
      onSuccess: (data) => {
        invalidateConnections();

        if (!data.cancelCloud?.success) {
          dispatch(
            queueNotification(
              data.cancelCloud?.error ||
                copy.components.endpoints.cancelEndpointDialog.notifications
                  .error
            )
          );
          return;
        }

        dispatch(
          queueMessage({
            intent: BlueprintIntent.SUCCESS,
            message:
              copy.components.endpoints.cancelEndpointDialog.notifications
                .success,
          })
        );
        handleClose();
      },
    }
  );

  const handleClose = () => {
    dispatch(closedDialog());
    setValue('');
  };

  const handleSubmit = () => {
    if (
      value.trim() !== CANCEL_CONFIRMATION ||
      isLoading ||
      !connection?.id ||
      !connection.cloud?.id
    ) {
      return;
    }

    cancelCloud({
      input: {
        cloud_id: connection.cloud.id,
        connection_id: connection.id,
      },
    });
  };

  const isSubmitDisabled = value.trim() !== CANCEL_CONFIRMATION || isLoading;

  const { warning } = copy.components.endpoints.cancelEndpointDialog;

  return (
    <Modal
      canOutsideClickClose={false}
      className="endpoint-dialog"
      footerActions={
        <>
          <Button
            onClick={handleClose}
            size="sm"
            testId={CANCEL_ENDPOINT_DIALOG_CANCEL}
            variant="ghost-gray"
          >
            {copy.components.endpoints.cancelEndpointDialog.buttons.cancel}
          </Button>
          <Button
            disabled={isSubmitDisabled}
            leftIcon={isLoading ? LoadingIcon : undefined}
            onClick={handleSubmit}
            size="sm"
            testId={CANCEL_ENDPOINT_DIALOG_SUBMIT}
            variant="primary-gray"
          >
            {copy.components.endpoints.cancelEndpointDialog.buttons.submit}
          </Button>
        </>
      }
      headerSize="lg"
      isOpen={Boolean(isOpen && connection)}
      onClose={handleClose}
      title={copy.components.endpoints.cancelEndpointDialog.title}
    >
      <div className="endpoint-dialog-body">
        <Callout icon={null} intent={BlueprintIntent.DANGER}>
          {warning.pre}
          <strong>{warning.strong}</strong>
          {warning.post(connection?.endpoint || '')}
        </Callout>

        <Input
          autoFocus
          clearable={false}
          label={copy.components.endpoints.inputs.cancel.label}
          onChange={(e) => setValue(e.target.value)}
          size="md"
          testId={CANCEL_ENDPOINT_DIALOG_INPUT}
          value={value}
        />
      </div>
    </Modal>
  );
};
